import {
  columnPinningFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  tableFeatures,
} from "@tanstack/react-table";
import { NetworkPortfolioManagementProject } from "@app/network-portfolio-management/interfaces";
import {
  GapOverviewActiveCell,
  GapOverviewApprovedCell,
  GapOverviewOpportunityCell,
  GapOverviewPendingCell,
} from "@app/network-portfolio-management/components/project-table/GapOverviewCell";
import {
  AccuracyAccuratePercentCell,
  AccuracyAttestationRateCell,
  AccuracyEnrichedPercentCell,
  AccuracyInaccuratePercentCell,
  AccuracyIndexScoreCell,
  AccuracySkippedPercentCell,
  AccuracySuspectPercentCell,
  ActionMenuCell,
  AdequacyApprovedExceptionsCountCell,
  AdequacyIndexScoreCell,
  AdequacyMetCountCell,
  AdequacyMetPercentCell,
  AdequacyTotalGapCountCell,
  AdequacyTotalGapPercentCell,
  CountiesCountCell,
  DateCell,
  DualMembershipCell,
  ProjectStatusCell,
  ProjectTitleCell,
  SelectCell,
  TagsCell,
  TemplateNameCell,
  ZipCodesCountCell,
} from "@app/network-portfolio-management/components/project-table/ProjectTableCell";

export const features = tableFeatures({
  columnPinningFeature,
  // columnSizingFeature is needed for getSize/getTotalSize/getStart, used by the shared
  // virtualized-table components
  columnSizingFeature,
  columnVisibilityFeature,
});

const columnHelper = createColumnHelper<typeof features, NetworkPortfolioManagementProject>();

export interface ProjectTableColumnGroup {
  columns: { id: string; label: string }[];
  groupId: string;
  groupLabel: string;
}

/**
 * Single source of truth for the column-visibility picker's grouping. Kept adjacent to
 * createProjectTableColumns() deliberately — when adding/removing/renaming a column below, update
 * this list too. Excludes "select" and "projectTitle" (always visible, never shown in the picker).
 */
export const PROJECT_TABLE_COLUMN_VISIBILITY_GROUPS: ProjectTableColumnGroup[] = [
  {
    columns: [
      { id: "lastRunDate", label: "Last Run Date" },
      { id: "runStatus", label: "Run Status" },
      { id: "counties", label: "Counties" },
      { id: "zipCodes", label: "Zip Codes" },
      { id: "template", label: "Template" },
      { id: "tags", label: "Tags" },
    ],
    groupId: "general",
    groupLabel: "General",
  },
  {
    columns: [
      { id: "adequacyIndexScore", label: "Index Score" },
      { id: "adequacyMetCount", label: "Adequate County-Specialties" },
      { id: "adequacyMetPercent", label: "Adequate County-Specialties %" },
      { id: "adequacyTotalGapCount", label: "Total Gap Count (Current Service Area)" },
      { id: "adequacyTotalGapPercent", label: "Total Gap Count (Current Service Area) %" },
      { id: "adequacyApprovedExceptionsCount", label: "Approved Exceptions" },
      { id: "dualMembership", label: "Dual Membership" },
    ],
    groupId: "adequacy",
    groupLabel: "Adequacy",
  },
  {
    columns: [
      { id: "gapOverviewActive", label: "Active" },
      { id: "gapOverviewPending", label: "Pending" },
      { id: "gapOverviewApproved", label: "Approved" },
      { id: "gapOverviewOpportunity", label: "Opportunity" },
    ],
    groupId: "gapOverview",
    groupLabel: "Gap Overview",
  },
  {
    columns: [
      { id: "accuracyIndexScore", label: "Index Score" },
      { id: "attestationRatePercent", label: "Attestation Rate" },
      { id: "accuratePercent", label: "Accurate" },
      { id: "inaccuratePercent", label: "Inaccurate" },
      { id: "suspectPercent", label: "Suspect" },
      { id: "enrichedPercent", label: "Enriched" },
      { id: "skippedPercent", label: "Skipped" },
    ],
    groupId: "accuracy",
    groupLabel: "Accuracy",
  },
];

interface CreateProjectTableColumnsOptions {
  selectionEnabled: boolean;
}

/**
 * Columns for Project row view, order matters.
 */
export const createProjectTableColumns = ({
  selectionEnabled,
}: CreateProjectTableColumnsOptions): ReturnType<typeof columnHelper.columns> =>
  columnHelper.columns([
    columnHelper.display({
      cell: ({ row }) => <ActionMenuCell project={row.original} />,
      enableHiding: false,
      header: "",
      id: "actions",
      size: 40,
    }),
    ...(selectionEnabled
      ? [
          columnHelper.display({
            cell: ({ row }) => <SelectCell project={row.original} />,
            enableHiding: false,
            header: "",
            id: "select",
            size: 48,
          }),
        ]
      : []),
    columnHelper.display({
      cell: ({ row }) => <ProjectTitleCell project={row.original} />,
      enableHiding: false,
      header: "Project Title",
      id: "projectTitle",
      size: 400,
    }),
    columnHelper.accessor("lastModifiedDate", {
      cell: ({ getValue }) => <DateCell dateString={getValue()} />,
      header: "Last Run Date",
      id: "lastRunDate",
      size: 110,
    }),
    columnHelper.accessor("status", {
      cell: ({ row }) => <ProjectStatusCell project={row.original} />,
      header: "Run Status",
      id: "runStatus",
      size: 165,
    }),
    columnHelper.display({
      cell: () => <CountiesCountCell />,
      header: "Counties",
      id: "counties",
      size: 100,
    }),
    columnHelper.display({
      cell: () => <ZipCodesCountCell />,
      header: "Zip Codes",
      id: "zipCodes",
      size: 100,
    }),
    columnHelper.accessor("templateName", {
      cell: ({ getValue }) => <TemplateNameCell templateName={getValue()} />,
      header: "Template",
      id: "template",
      size: 200,
    }),
    columnHelper.group({
      columns: [
        columnHelper.display({
          cell: () => <AdequacyIndexScoreCell />,
          header: "Index Score",
          id: "adequacyIndexScore",
          size: 150,
        }),
        columnHelper.display({
          // Header matches card view's AdequacyOverview label for this field verbatim (see
          // AdequacyOverview.tsx's STATUS_KEYS). Headers have no truncation/tooltip fallback, but
          // the header cell has no `white-space: nowrap` either — it wraps onto multiple lines by
          // default (same as "Approved Exceptions" below), so this stays sized like the table's
          // other columns rather than being widened to force a single line.
          cell: () => <AdequacyMetCountCell />,
          header: "Adequate County-Specialties",
          id: "adequacyMetCount",
          size: 160,
        }),
        columnHelper.display({
          cell: () => <AdequacyMetPercentCell />,
          header: "Adequate County-Specialties %",
          id: "adequacyMetPercent",
          size: 160,
        }),
        columnHelper.display({
          // Header matches card view's AdequacyOverview label for this field verbatim — wraps
          // onto multiple lines like "Approved Exceptions" (see comment above).
          cell: () => <AdequacyTotalGapCountCell />,
          header: "Total Gap Count (Current Service Area)",
          id: "adequacyTotalGapCount",
          size: 160,
        }),
        columnHelper.display({
          cell: () => <AdequacyTotalGapPercentCell />,
          header: "Total Gap Count (Current Service Area) %",
          id: "adequacyTotalGapPercent",
          size: 160,
        }),
        columnHelper.display({
          cell: () => <AdequacyApprovedExceptionsCountCell />,
          header: "Approved Exceptions",
          id: "adequacyApprovedExceptionsCount",
          size: 160,
        }),
        columnHelper.display({
          cell: ({ row }) => <DualMembershipCell project={row.original} />,
          header: "Dual Membership",
          id: "dualMembership",
          size: 160,
        }),
      ],
      header: "Adequacy",
      id: "adequacy",
    }),
    columnHelper.group({
      columns: [
        columnHelper.display({
          cell: () => <GapOverviewActiveCell />,
          header: "Active",
          id: "gapOverviewActive",
          size: 90,
        }),
        columnHelper.display({
          cell: () => <GapOverviewPendingCell />,
          header: "Pending",
          id: "gapOverviewPending",
          size: 90,
        }),
        columnHelper.display({
          cell: () => <GapOverviewApprovedCell />,
          header: "Approved",
          id: "gapOverviewApproved",
          size: 90,
        }),
        columnHelper.display({
          cell: () => <GapOverviewOpportunityCell />,
          header: "Opportunity",
          id: "gapOverviewOpportunity",
          size: 100,
        }),
      ],
      header: "Gap Overview",
      id: "gapOverview",
    }),
    columnHelper.group({
      columns: [
        columnHelper.display({
          cell: () => <AccuracyIndexScoreCell />,
          header: "Index Score",
          id: "accuracyIndexScore",
          size: 150,
        }),
        columnHelper.display({
          cell: () => <AccuracyAttestationRateCell />,
          header: "Attestation Rate",
          id: "attestationRatePercent",
          size: 150,
        }),
        columnHelper.display({
          cell: () => <AccuracyAccuratePercentCell />,
          header: "Accurate",
          id: "accuratePercent",
          size: 100,
        }),
        columnHelper.display({
          cell: () => <AccuracyInaccuratePercentCell />,
          header: "Inaccurate",
          id: "inaccuratePercent",
          size: 100,
        }),
        columnHelper.display({
          cell: () => <AccuracySuspectPercentCell />,
          header: "Suspect",
          id: "suspectPercent",
          size: 100,
        }),
        columnHelper.display({
          cell: () => <AccuracyEnrichedPercentCell />,
          header: "Enriched",
          id: "enrichedPercent",
          size: 100,
        }),
        columnHelper.display({
          cell: () => <AccuracySkippedPercentCell />,
          header: "Skipped",
          id: "skippedPercent",
          size: 100,
        }),
      ],
      header: "Accuracy",
      id: "accuracy",
    }),
    columnHelper.accessor("tags", {
      cell: ({ getValue }) => <TagsCell tags={getValue()} />,
      header: "Tags",
      id: "tags",
      size: 300,
    }),
  ]);
